import React, { useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  firstError,
  hasError,
  validateModule,
  validateModuleField,
} from "../moduleValidation";
import { useIsAdmin, useIsCreatorManager } from "../../../auth/authUtils";
import {
  BiblioboardLink,
  useBiblioboardUrl,
} from "../../common/BiblioboardLink";
import { anyTrue } from "../../../util/ext";
import { MediaContext } from "../../common/media-context";

import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";

import EditColumn from "../../common/EditColumn";
import CopyIcon from "../../common/CopyIcon";
import EditModuleDetailsCheckboxes from "./EditModuleDetailsCheckboxes";
import ContentGroupOwner from "../../common/ContentGroupOwner";

const useStyles = makeStyles(theme => ({
  root: {
    padding: 0,
  },

  column: {
    padding: "0 50px 0 50px",
  },

  charCount: {
    width: "100%",
    marginTop: 5,
    textAlign: "right",
  },

  title: {
    fontSize: "1.8rem",
  },

  rte: {
    borderWidth: 1,
  },
}));

const validator = {
  validateField: validateModuleField,
  validate: validateModule,
};

const EditModuleInfoColumn = props => {
  const classes = useStyles();
  const context = useContext(MediaContext);
  const isAdmin = useIsAdmin();
  const isCreatorManager = useIsCreatorManager();
  const [url, showUrl] = useBiblioboardUrl(context.media);

  const formBuilder = handlers => {
    return (
      <div>
        <Grid container>
          <Grid
            className={classes.column}
            container
            item
            direction="column"
            spacing={4}
            xs={8}
          >
            <Grid item>
              <TextField
                id="module-name"
                InputProps={{
                  classes: { root: classes.title },
                }}
                fullWidth
                label="Title"
                value={context.media.name}
                onChange={event => {
                  handlers.onTextChange(["name"])(event);
                  if (!isAdmin) {
                    handlers.onTextChange(["displayName"])(event);
                  }
                }}
                error={hasError(context.errors, "name")}
                helperText={firstError(context.errors, "name")}
                onBlur={() => handlers.onBlur("name", context.media.name)}
              />
            </Grid>
            <Grid item>
              <TextField
                id="module-sub-name"
                fullWidth
                maxLength={255}
                label="Subtitle"
                value={context.media.subName}
                onChange={handlers.onTextChange(["subName"])}
                error={hasError(context.errors, "subName")}
                helperText={firstError(context.errors, "subName")}
                onBlur={() => handlers.onBlur("subName", context.media.subName)}
              />
            </Grid>
            {isAdmin && (
              <Grid item>
                <TextField
                  id="display-name"
                  fullWidth
                  label="Display Name"
                  value={context.media.displayName}
                  onChange={handlers.onTextChange(["displayName"])}
                  error={hasError(context.errors, "displayName")}
                  helperText={firstError(context.errors, "displayName")}
                  onBlur={() =>
                    handlers.onBlur("displayName", context.media.displayName)
                  }
                />
              </Grid>
            )}
            {showUrl && (
              <Grid item>
                <TextField
                  id="biblioboard-url-module"
                  fullWidth
                  disabled
                  label="BiblioBoard URL"
                  value={url}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <BiblioboardLink media={context.media} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            )}
            {isAdmin && (
              <Grid item>
                <TextField
                  id="default-curation-name"
                  fullWidth
                  maxLength={100}
                  label="Default Curation Name"
                  value={context.media.defaultCurationName || ""}
                  onChange={handlers.onTextChange(["defaultCurationName"])}
                  error={hasError(context.errors, "defaultCurationName")}
                  helperText={firstError(context.errors, "defaultCurationName")}
                  onBlur={() =>
                    handlers.onBlur(
                      "defaultCurationName",
                      context.media.defaultCurationName,
                    )
                  }
                />
                <div className={classes.charCount}>
                  {context.media.defaultCurationName
                    ? context.media.defaultCurationName.length
                    : 0}
                  /100
                </div>
              </Grid>
            )}
          </Grid>
          <Grid container item direction="column" spacing={4} xs={4}>
            <Grid item>
              <TextField
                id="module-id"
                disabled
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <CopyIcon value={context.media.id} text="Module ID" />
                    </InputAdornment>
                  ),
                }}
                fullWidth
                label="Module ID"
                value={context.media.id}
                onChange={handlers.onTextChange(["id"])}
                error={hasError(context.errors, "id")}
                helperText={firstError(context.errors, "id")}
                onBlur={() => handlers.onBlur("id", context.media.id)}
              />
            </Grid>

            <Grid item>
              <ContentGroupOwner />
            </Grid>

            {anyTrue([isAdmin, isCreatorManager]) && (
              <Grid item>
                <TextField
                  id="identifier-internal-code"
                  fullWidth
                  maxLength={255}
                  label="Identifier (internal code)"
                  value={context.media.internalCode || ""}
                  onChange={handlers.onTextChange(["internalCode"])}
                  error={hasError(context.errors, "internalCode")}
                  helperText={firstError(context.errors, "internalCode")}
                  onBlur={() =>
                    handlers.onBlur("internalCode", context.media.internalCode)
                  }
                />
                <div className={classes.charCount}>
                  {context.media.internalCode?.length ?? 0}/255
                </div>
              </Grid>
            )}
            {isAdmin && <EditModuleDetailsCheckboxes />}
          </Grid>
        </Grid>
      </div>
    );
  };

  return (
    <EditColumn
      className={classes.root}
      validator={validator}
      formBuilder={formBuilder}
    />
  );
};

export default EditModuleInfoColumn;
